/**
 * Passive instrumentation inside the real pose loop (use-predict-worker.jsx).
 *
 * The exercise loop calls the report* functions at fixed points. With no sink
 * installed — always the case outside the device-performance diagnostic and
 * the dev-only live probe — each call is one null check and returns, so the
 * production loop is unchanged. A sink is installed for the length of one
 * measurement.
 *
 * Accounting contract (what makes the numbers add up):
 *   Every setInterval tick of the loop reports exactly ONE tick outcome:
 *     'busy'       the previous tick is still running (capture/detect/deliver)
 *     'not-ready'  the loop could not start a frame (paused, video/runtime not ready)
 *     'throttled'  the adaptive throttle reused the last result instead of detecting
 *     'admitted'   a fresh frame was admitted for capture + inference
 *   Every 'admitted' tick later reports exactly one frame record (reportPoseFrame)
 *   unless the sink is removed while it is still in flight.
 *   Every frame the loop hands on (fresh or reused) reports one delivery.
 *
 * A sink must never be able to break the loop, so every call is guarded.
 */
let sink = null;

/**
 * @param {{ onTick?: Function, onFrame?: Function, onDelivery?: Function }} nextSink
 * @returns {() => void} removes this sink (and only this sink)
 */
export function setPoseProbeSink(nextSink) {
  sink = nextSink;
  return () => {
    if (sink === nextSink) {
      sink = null;
    }
  };
}

/** True while a sink is installed — lets the loop skip building probe-only values. */
export function isPoseProbeActive() {
  return sink !== null;
}

/**
 * One tick outcome (see the accounting contract above).
 * @param {'busy'|'not-ready'|'throttled'|'admitted'} kind
 * @param {{ reason?: string, throttleLevel?: number }} [detail]
 */
export function reportPoseTick(kind, detail) {
  if (!sink) {
    return;
  }
  try {
    sink.onTick?.(kind, performance.now(), detail);
  } catch (_error) {
    // Instrumentation must never break the pose loop.
  }
}

/**
 * What happened to one admitted frame. All `*At` values are main-thread
 * performance.now() timestamps; `timing` comes from the runtime (see
 * workers/pose-runtime.js) and holds durations in ms.
 * @param {{
 *   outcome: 'completed'|'dropped'|'capture-error'|'detect-error'|'discarded',
 *   admittedAt: number,
 *   captureEndedAt: number|null,
 *   resultAt: number|null,
 *   inferenceMs?: number|null,
 *   timing?: object|null,
 *   hasPose?: boolean,
 * }} sample
 */
export function reportPoseFrame(sample) {
  if (!sink) {
    return;
  }
  try {
    sink.onFrame?.(sample);
  } catch (_error) {
    // Instrumentation must never break the pose loop.
  }
}

/**
 * One frame handed on by finishTick (skeleton drawn, payload built and sent).
 * @param {{ fresh: boolean, startedAt: number, endedAt: number }} sample
 *   fresh: carries a new inference result (false = throttled/dropped reuse or an error frame).
 */
export function reportPoseDelivery(sample) {
  if (!sink) {
    return;
  }
  try {
    sink.onDelivery?.(sample);
  } catch (_error) {
    // Instrumentation must never break the pose loop.
  }
}
